import { AttachmentFile } from '@rightship-tech/common-ui/models/attachments.models';
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { NotificationClientService, NotificationType } from "@rightship-tech/common-ui/services/basic/notification-client.service";
import { UserClaimsService } from "@rightship-tech/common-ui/services/basic/user-claims.service";
import { autoinject, computedFrom, transient } from "aurelia-framework";
import { BindingSignaler } from "aurelia-templating-resources";
import { Claims } from "../../../../constants/claims.constants";
import { DeficiencyDetails } from "../../../../models/psc.models";
import { PscService } from "../../../../services/psc.service";
import { Router } from "aurelia-router";
import { MyVesselsRouteConstants } from "../../../../constants/route.constants";
import { EmailRecipientTypeEnum } from "../../../../models/common.models";
import { TaskService } from '@rightship-tech/tasks-ui/services/task.service';
import { UserClientService } from '@rightship-tech/common-ui/services/basic/user-client.service';
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { CustomValidationRenderer } from '@rightship-tech/common-ui/client-validation-system/custom-validation-renderer';
import { RegexConstants } from '@rightship-tech/common-ui/constants/regex.constants';
import moment from 'moment';

@autoinject
@transient()
export class SubmitCloseOutInformation {

    private _params: any;
    private _hasRsUserPermission = false;
    private _validator: ValidationController;
    public entityId: number;
    public orderGuid: string;
    public isPriority: boolean;
    public entityTypeId: number;
    public vesselName: string;
    public isSubmitted: boolean;
    public taskDate: string;
    public description: string;
    public showMoreText: { [key: string]: boolean } = {};
    public showAttachmentsSection: boolean = false;
    public pscDeficiencyDetails: DeficiencyDetails[] = [];
    public entityTypes = EntityTypes;
    public isPSC: boolean;
    public files: AttachmentFile[];
    public correspondenceEmails: { email: string, emailRecipientTypeId: EmailRecipientTypeEnum.To, validated?: boolean }[] = [];
    public correspondenceMessage: string;
    public subtaskCorrespondenceId: number;

    constructor(private notificationClientService: NotificationClientService, private pscService: PscService, 
        private bindingSignaler: BindingSignaler, private userClaimsService: UserClaimsService, private router: Router,
        private taskService: TaskService, private userClientService: UserClientService, validationControllerFactory: ValidationControllerFactory) {
            this._validator = validationControllerFactory.createForCurrentScope();
            this._validator.addRenderer(new CustomValidationRenderer());
    }

    activate(params: any): void {
        this._params = params;
        this.orderGuid = params.orderGuid;
        this.isPriority = params.isPriority;
        this.entityId = params.entityId;
        this.vesselName = params.vesselName;
        this.entityTypeId = params.entityTypeId;
        this.taskDate = this.formatDateTime(new Date(params.date));
        this.description = params.description;
        this.isPSC = params.entityTypeId == EntityTypes.PSC;
        if(this.isPSC)
        {
            this.getDeficiencyDetails();
        }
        this.showAttachmentsSection = true;
        this._hasRsUserPermission = this.userClaimsService.hasPermission(Claims.RightShipUser);

        if (!this.correspondenceEmails)
            this.correspondenceEmails = [];

        if (this.userClientService && this.userClientService.currentUser.user && this.userClientService.currentUser.user.email)
            this.correspondenceEmails.push({ email: this.userClientService.currentUser.user.email, emailRecipientTypeId: EmailRecipientTypeEnum.To });
    }

    async getDeficiencyDetails() {
        let id = this._params && this._params.entityId? this._params.entityId: this._params.id;
        let result = await this.pscService.getDeficiencyDetails(id);
        this.pscDeficiencyDetails = result;
    }

    getLongText(fullText: string, key: string) {
        if (this.showMoreText && Object.keys(this.showMoreText).indexOf(key) > -1) {
            if (this.showMoreText[key])
                return fullText;
        }

        return fullText ? fullText.substring(0, 100) + (fullText.length > 100 ? '...' : '') : '';
    }

    removeEmail(index: number) {
        if (this.correspondenceEmails.length == 0)
            return;

        this.correspondenceEmails.splice(index, 1);
    }

    addEmail() {
        if (!this.correspondenceEmails) {
            this.correspondenceEmails = [];
        }

        this.correspondenceEmails.push({ email: "", emailRecipientTypeId: EmailRecipientTypeEnum.To });
    }

    public validateOnSubmit(): boolean {
        if (this.files && this.files.filter(x => !x.attachmentTypeId).length > 0) {
            this.notificationClientService.show('', 'Attachment type is required', NotificationType.Error)
            return;
        }

        if (this.correspondenceMessage == "") {
            this.notificationClientService.show('Validation Error', 'Closed out form cannot be submitted without message', NotificationType.Error)
            return;
        }

        if ((!this.files || this.files.length == 0) && !this._hasRsUserPermission) {
            this.notificationClientService.show("Validation Error", "Closed out form cannot be submitted without documents", NotificationType.Error);
            return;
        }
        return true;
    }

    getEmailRules(){
        let emailRegex = new RegExp(RegexConstants.Patterns.EmailRfc5322);

        this.correspondenceEmails.forEach((email, index) => {
            ValidationRules
                .ensure("email")
                .required()
                .withMessage("Email is required")
                .satisfies(email => {
                    return emailRegex.test(email)
                })
                .withMessage("Email is not in correct format")
                .on(email);
        });
    }

    toggleReadMore(key: string) {
        if (!this.showMoreText)
            this.showMoreText = {};

        this.showMoreText[key] = Object.keys(this.showMoreText).indexOf(key) > -1 ? !this.showMoreText[key] : true;

        this.bindingSignaler.signal('read-more-toggled');
    }

    //Go back to pending tasks
    goBackToPendingTasks() {
        this.router.navigate(MyVesselsRouteConstants.Dashboard);
    }

    //Cancel
    cancel() {
        this.router.navigateBack();
    }

    public async submit() {
        try {
            if (this.correspondenceMessage && !$(this.correspondenceMessage).get(0).innerText.trim()) {
                this.correspondenceMessage = "";
            }
            if (!this.validateOnSubmit())
                return;

            //add email validation rules
            this.getEmailRules();
            let validator = await this._validator.validate();
            if (validator.valid) {
                await this.addSubtask(true);
                this.correspondenceMessage = "";
                this.subtaskCorrespondenceId = null;
                this.files = [];
    
                //If coming from Submit Data, do not close dialog and instead show "Thank you for your submission" in submit-close-out-information-dialog.html
                this.isSubmitted = true;
                
                this.notificationClientService.show("Contact RightShip Operations", "Thank you for your message. You will receive an email when RightShip respond back to you.", NotificationType.Success);   
            }
        } catch(er) {
            console.error(er);
            this.notificationClientService.show("Contact RightShip Operations", "Failed to send message.", NotificationType.Error);
        }
    }

    async addSubtask(submit: boolean) {
        let snippet = "Close Out Submitted";
        return await this.taskService.addSubtask({
            entityId: this.entityId,
            entityTypeId: this.entityTypeId,
            emails: this.correspondenceEmails.map(x => {
                return { email: x.email, emailRecipientTypeId: x.emailRecipientTypeId }
            }),
            isPriority: this.isPriority,
            orderGuid: this.orderGuid,
            isInternal: false,
            skipInvitation: true,
            isCustomerRFI: true,
            changeStatusToPendingReview: true,
            changeStatusToAwaitingResponse: false,
            message: this.correspondenceMessage,
            resendEmail: false,
            comment: "",
            attachments: [],
            files: this.getAttachmentTypes(),
            statusSnippet: snippet,
            name: `${snippet}<br/>User: ${this.userClientService.currentUser.user.fullName}<br/>Email: ${this.userClientService.currentUser.user.email}`,
            submit: submit,
            subtaskId: null,
            subtaskTypes: [],
            subtaskCorrespondenceId: this.subtaskCorrespondenceId,
            vetTaskIds: [],
            dataSourceId: 0
        });
    }

    getAttachmentTypes(){
        if(this.files && this.files.length > 0)
        {
            let attachmentFiles = this.files.map(c=>{
                return {attachementTypeId:c.attachmentTypeId, file: c as File}
            });
            return attachmentFiles;
        }

        return [];
    }

    formatDateTime(dateToFormat: Date): string {
        return dateToFormat ? moment(dateToFormat).format('DD MMM YYYY') : '';
    }

    @computedFrom('isPSC')
    get submitCloseOutTitle(): string {
        return this.isPSC ? "Submit PSC Close-out" : "Submit Incident Close-out";
    }

    @computedFrom('isPSC')
    get submitCloseOutSuccessTitle(): string {
        return this.isPSC ? "PSC Close-out has been submitted" : "Incident Close-out has been submitted";
    }
}
